import { useCallback, useEffect, useMemo, useState } from "react";
import { FinanceApi } from "../api/finance_api";
import {
  FinanceDashboardState,
  FinanceData,
  financeTimeRangeOptions,
  NumericValue,
  TimeRange,
} from "../model/finance_model";

export const timeRangeOptions = financeTimeRangeOptions;

export const toNumber = (value: NumericValue) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = Number(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const formatCurrency = (value: NumericValue, fractionDigits = 2) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(toNumber(value));

export const formatNumber = (value: NumericValue, fractionDigits = 0) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(toNumber(value));

export const formatPercent = (value: NumericValue, fractionDigits = 1) =>
  `${formatNumber(value, fractionDigits)}%`;

export const formatSignedPercent = (value: NumericValue) => {
  const numericValue = toNumber(value);
  const sign = numericValue > 0 ? "+" : "";
  return `${sign}${formatPercent(numericValue, 2)}`;
};

export const getTrendClasses = (value: NumericValue) =>
  toNumber(value) >= 0
    ? "bg-green-50 text-green-700"
    : "bg-red-50 text-red-700";

export const clampPercent = (value: NumericValue) =>
  Math.min(100, Math.max(0, toNumber(value)));

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const sortNumericEntries = (tiers: Record<string, NumericValue>) =>
  Object.entries(tiers).sort(
    ([, firstValue], [, secondValue]) =>
      toNumber(secondValue) - toNumber(firstValue),
  );

const buildDashboardViewData = (financeData: FinanceData) => {
  const revenueByTier = sortNumericEntries(
    financeData.subscription_breakdown.revenue_by_tier || {},
  );
  const activeSubscriptionsByTier = sortNumericEntries(
    financeData.subscription_breakdown.active_subscriptions_by_tier || {},
  );
  const totalActiveSubscriptions = activeSubscriptionsByTier.reduce(
    (total, [, count]) => total + toNumber(count),
    0,
  );

  const financialMetrics = financeData.financial_metrics;
  const operationalCosts = financeData.operational_costs;
  const aiMetrics = financeData.ai_metrics;
  const creditsMetrics = financeData.credits_metrics;
  const subscriptionBreakdown = financeData.subscription_breakdown;
  const filteredAiMetrics = aiMetrics.filtered_period || {};
  const lifetimeAiMetrics = aiMetrics.lifetime_summary || {};
  const tokenUsage = aiMetrics.token_usage || {};
  const tokenAverages = tokenUsage.lifetime_averages_per_resume || {};
  const costBreakdown = aiMetrics.cost_breakdown || {};
  const rates = costBreakdown.rates || {};

  return {
    financeData,
    financialMetrics,
    operationalCosts,
    aiMetrics,
    creditsMetrics,
    subscriptionBreakdown,
    topCompanies: financeData.top_companies,
    filteredAiMetrics,
    lifetimeAiMetrics,
    tokenUsage,
    tokenAverages,
    costBreakdown,
    rates,
    revenueByTier,
    activeSubscriptionsByTier,
    totalActiveSubscriptions,
    creditUsageRate: clampPercent(creditsMetrics.credit_usage_rate),
  };
};

export const useFinanceViewModel = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");
  const [financeData, setFinanceData] = useState<FinanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFinanceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!localStorage.getItem("access_token")) {
        setFinanceData(null);
        setError("Authentication required");
        return;
      }

      const data = await FinanceApi.getDashboard(timeRange);
      setFinanceData(data);
    } catch (requestError) {
      console.error("Error loading finance data:", requestError);
      setFinanceData(null);
      setError(
        getErrorMessage(requestError, "Failed to load financial data"),
      );
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    void loadFinanceData();
  }, [loadFinanceData]);

  const state: FinanceDashboardState = useMemo(
    () => ({
      timeRange,
      financeData,
      loading,
      error,
    }),
    [error, financeData, loading, timeRange],
  );

  const dashboard = useMemo(
    () => (financeData ? buildDashboardViewData(financeData) : null),
    [financeData],
  );

  return {
    state,
    dashboard,
    timeRangeOptions,
    setTimeRange,
    reloadFinanceData: loadFinanceData,
  };
};
